import { useState, useRef, useEffect } from 'react';
import type { DeckZoneGroup } from '@sleeved/lib';
import {
  DECK_GRID_CARD_ASPECT as CARD_ASPECT,
  DECK_GRID_CARD_GAP as CARD_GAP,
  DECK_GRID_ZONE_GAP as ZONE_GAP,
  DECK_GRID_ZONE_ROW_GAP as ZONE_ROW_GAP,
  DECK_GRID_ZONE_LABEL_COL_W as ZONE_LABEL_COL_W,
} from '@sleeved/lib';
import { glass, glassText, glassBorder } from '../styles/overlayTokens';

interface DeckCardGridProps {
  groups: DeckZoneGroup[];  // pre-ordered by web compute; no reordering in overlay
  gameId?: string;          // for debugging logs only — no game-logic branching
  /**
   * Hard upper bound on cards-per-row. The fit-to-fill search will not exceed
   * this value even if it could still fit more rows. Defaults to 10.
   * Set per-game at the call site (e.g. grand-archive → 12) — this component
   * contains no game-branching logic itself.
   */
  maxPerRow?: number;
  /**
   * Force an exact cards-per-row, bypassing the fit-to-fill search. When set, the
   * grid always renders exactly this many columns (e.g. grand-archive → 12, so its
   * 12-card material zone sits on a single row). `maxPerRow` is ignored when set.
   */
  fixedPerRow?: number;
}

/**
 * Card image grid for the overlay deck-list view.
 *
 * Renders DeckZoneGroup[] as zone-label sections with a CSS-grid card area that
 * fills the full available column width at the largest uniform card size fitting
 * all zone rows within the available container height.
 *
 * Sizing algorithm (fit-to-fill):
 *   W = container.clientWidth - ZONE_LABEL_COL_W - ZONE_ROW_GAP
 *   H = container.clientHeight
 *   For perRow = PER_ROW_MIN, PER_ROW_MIN+1, … up to maxPerRow (prop; default 10):
 *     cardWidth  = (W - (perRow-1) * CARD_GAP) / perRow
 *     cardHeight = cardWidth / CARD_ASPECT
 *     if cardHeight < MIN_CARD_HEIGHT_PX → stop (floor hit; keep last valid dims,
 *       accept height overflow, outer overflow:hidden clips)
 *     if estimateContentHeight(perRow, cardHeight) ≤ H → use this perRow (done)
 *   Chooses the smallest perRow (= largest cards) whose content fits in H.
 *   If none fit before the floor, the last floor-valid perRow wins (clip allowed).
 *
 * Card rows are rendered as CSS grid `repeat(perRow, 1fr)` so each column is
 * exactly W/perRow — no right-side dead space.
 *
 * Zone labels: 40px vertical column on LEFT, writing-mode:vertical-rl +
 *   rotate(180deg) (bottom-to-top), zone name only — no card count.
 *
 * Quantity badges (×N) appear on cards with quantity > 1.
 * Image-load failures render a glassBorder.strong placeholder rect.
 *
 * This component contains NO game logic and imports NO game adapters.
 */

// ─── Layout constants ────────────────────────────────────────────────────────
// CARD_ASPECT, CARD_GAP, ZONE_GAP, ZONE_ROW_GAP, ZONE_LABEL_COL_W are
// imported from @sleeved/lib (DECK_GRID_* names) at the top of this file.
// The lib is the canonical source — update values there, not here.

/**
 * Broadcast legibility floor: card height never falls below this value.
 * Very large decks clip at this floor rather than shrink further — a visible
 * clip is better for broadcast than unreadable card art. overflow:hidden on the
 * outer container handles the clip.
 */
const MIN_CARD_HEIGHT_PX = 90; // px

/** Smallest cards-per-row to try (= largest possible cards). */
const PER_ROW_MIN = 5;

// ─── Height estimator ────────────────────────────────────────────────────────

/**
 * Estimates the total rendered height (px) of all zone card grids for a given
 * (perRow, cardHeight) pair. Called inside the fit-to-fill search loop only —
 * O(groups.length) per call; the loop runs until content fits or the floor is hit.
 *
 * Each zone contributes:  ceil(cards.length / perRow) rows × cardHeight
 *                       + max(0, rows-1) × CARD_GAP (row gaps inside the CSS grid)
 * Plus: max(0, zones-1) × ZONE_GAP for the flex-column gaps between zones.
 */
function estimateContentHeight(
  groups: DeckZoneGroup[],
  perRow: number,
  cardH: number,
): number {
  const zoneHeights = groups.reduce((sum, group) => {
    const rows = Math.ceil(group.cards.length / perRow);
    return sum + rows * cardH + Math.max(0, rows - 1) * CARD_GAP;
  }, 0);
  return zoneHeights + Math.max(0, groups.length - 1) * ZONE_GAP;
}

// ─── State type ──────────────────────────────────────────────────────────────

interface CardDims {
  /** Computed card width in px. Each row of perRow cards fills W exactly. */
  cardWidth: number;
  /** Computed card height in px = cardWidth / CARD_ASPECT. */
  cardHeight: number;
  /**
   * Cards per row — drives CSS grid `repeat(perRow, 1fr)`.
   * Smallest value (largest cards) whose total content height fits in H.
   */
  perRow: number;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function DeckCardGrid({ groups, gameId, maxPerRow = 10, fixedPerRow }: DeckCardGridProps) {
  const outerRef = useRef<HTMLDivElement>(null);

  // Sensible initial dims (roughly the old fixed constants) overwritten after
  // the first rAF measurement — the one-frame flicker is imperceptible at 60fps.
  const [dims, setDims] = useState<CardDims>({ cardWidth: 140, cardHeight: 196, perRow: 7 });

  if (gameId) {
    console.debug('[DeckCardGrid] rendering groups for gameId:', gameId, 'zone count:', groups.length);
  }

  // Fit-to-fill sizing: one rAF per groups/maxPerRow change.
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      if (!outerRef.current || groups.length === 0) return;

      const H = outerRef.current.clientHeight;
      // W = available card-area width: full container minus the label column and
      // the gap between that column and the card grid.
      const W = outerRef.current.clientWidth - ZONE_LABEL_COL_W - ZONE_ROW_GAP;
      if (H <= 0 || W <= 0) return;

      // Forced column count (e.g. grand-archive → 12, or a side-by-side per-zone count):
      // keep EXACTLY fixedPerRow columns, but bound the card size by BOTH width and height
      // so every row fits inside the component — cards shrink to fit the viewport rather than
      // overflowing/clipping at the bottom edge.
      if (fixedPerRow && fixedPerRow > 0) {
        // Width-fit: a row of fixedPerRow cards fills W exactly.
        const cwByWidth = (W - (fixedPerRow - 1) * CARD_GAP) / fixedPerRow;
        // Height-fit: total rows across all zones must fit in H. Vertical overhead that does
        // NOT scale with card size: inter-row gaps within each zone + inter-zone gaps.
        const totalRows = groups.reduce((sum, g) => sum + Math.ceil(g.cards.length / fixedPerRow), 0);
        const fixedOverhead =
          groups.reduce(
            (sum, g) => sum + Math.max(0, Math.ceil(g.cards.length / fixedPerRow) - 1) * CARD_GAP,
            0,
          ) + Math.max(0, groups.length - 1) * ZONE_GAP;
        const chByHeight = totalRows > 0 ? (H - fixedOverhead) / totalRows : cwByWidth / CARD_ASPECT;
        // Use the smaller constraint so cards never overflow the column width OR the viewport height.
        const cw = Math.max(1, Math.min(cwByWidth, chByHeight * CARD_ASPECT));
        setDims({ perRow: fixedPerRow, cardWidth: cw, cardHeight: cw / CARD_ASPECT });
        return;
      }

      // Initialise to PER_ROW_MIN (largest possible cards). The loop tries
      // successively larger perRow values until the content fits in H or the
      // legibility floor is hit — whichever comes first.
      let finalPerRow = PER_ROW_MIN;
      let finalCardWidth = (W - (PER_ROW_MIN - 1) * CARD_GAP) / PER_ROW_MIN;
      let finalCardHeight = finalCardWidth / CARD_ASPECT;

      // Search from PER_ROW_MIN upward, up to maxPerRow. Three termination criteria:
      //   1. Legibility floor (MIN_CARD_HEIGHT_PX) — cards would be unreadably small.
      //   2. Column cap (maxPerRow) — broadcast density limit; set per-game at the call site.
      //   3. Height fit — content fits in H; found the optimal (largest cards that fit).
      // If none of (2) or (3) triggers before (1), the last floor-valid perRow wins and
      // the outer overflow:hidden clips any height overshoot.
      // The old PER_ROW_MAX=10 hard cap was global and caused large decks to fall through
      // to a post-hoc transform:scale() that visually shrank the grid to ~60% column width
      // — leaving a dead gap on the right. That scale path has been removed; this per-call
      // maxPerRow prop gives the caller control without re-introducing the width defect.
      for (let n = PER_ROW_MIN; ; n++) {
        const cw = (W - (n - 1) * CARD_GAP) / n;
        const ch = cw / CARD_ASPECT;

        // Legibility floor: this perRow would produce cards shorter than the
        // broadcast minimum. Keep the last valid dims (recorded from n-1) and
        // break — the outer overflow:hidden clips any height overshoot.
        if (ch < MIN_CARD_HEIGHT_PX) break;

        // Record as the current best (always above the floor here).
        finalPerRow = n;
        finalCardWidth = cw;
        finalCardHeight = ch;

        // Column cap: do not exceed maxPerRow. Accept height overflow (clipped by
        // overflow:hidden on the outer container) rather than going denser than the
        // per-game broadcast limit. This is intentional for large decks on broadcast.
        if (n >= maxPerRow) break;

        // If this perRow's estimated total height fits within H, we found the
        // optimal: smallest perRow (= largest cards) that still fits vertically.
        if (estimateContentHeight(groups, n, ch) <= H) break;

        // Content still overflows — try one more card per row (smaller cards,
        // fewer rows, less total height) and continue the search.
      }

      setDims({ perRow: finalPerRow, cardWidth: finalCardWidth, cardHeight: finalCardHeight });
    });
    return () => cancelAnimationFrame(rafId);
  }, [groups, maxPerRow, fixedPerRow]);

  // Empty-state: deck is loaded but produced zero non-empty zones.
  if (groups.length === 0) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: '100%', height: '100%', fontSize: 16, color: glassText.secondary,
      }}>
        No deck loaded
      </div>
    );
  }

  return (
    <div
      ref={outerRef}
      className="dcg-outer"
      style={{ width: '100%', height: '100%', overflow: 'hidden' }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: ZONE_GAP,
          width: '100%',
        }}
      >
        {groups.map(group => (
          <div key={group.zoneId} style={{ display: 'flex', flexDirection: 'row', gap: ZONE_ROW_GAP }}>
            {/* Left: vertical zone label — name only (no count).
                writing-mode:vertical-rl + rotate(180deg) renders bottom-to-top,
                matching deckImageExport.ts ctx.rotate(-Math.PI/2) direction.
                Width is ZONE_LABEL_COL_W=40px; height stretches to match the card grid. */}
            <div style={{
              width: ZONE_LABEL_COL_W,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <div style={{
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
                fontSize: 15,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: glassText.secondary,
                whiteSpace: 'nowrap',
              }}>
                {group.label}
              </div>
            </div>

            {/* Right: CSS grid sized to the COMPUTED cardWidth (not 1fr) so every cell keeps the
                card's portrait aspect ratio. When fit-to-fill / fixedPerRow is width-constrained,
                cardWidth === W/perRow and the row fills edge-to-edge (no dead space). When it is
                height-constrained (all rows must fit the viewport), cardWidth shrinks below W/perRow
                so the whole card scales down — never cropped — leaving horizontal space at the edge. */}
            <div style={{
              flex: 1,
              display: 'grid',
              gridTemplateColumns: `repeat(${dims.perRow}, ${dims.cardWidth}px)`,
              gap: CARD_GAP,
            }}>
              {group.cards.map((card) => (
                <CardTile
                  key={`${group.zoneId}-${card.name}`}
                  card={card}
                  width={dims.cardWidth}
                  height={dims.cardHeight}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CardTile ────────────────────────────────────────────────────────────────

interface CardTileProps {
  card: { name: string; quantity: number; imageUrl: string | null };
  /**
   * Card width in px — passed as HTML attr on <img> for CLS prevention (browser
   * allocates the slot before the image loads). CSS uses width:100% to fill the
   * grid cell, so fractional cardWidth values don't cause sub-pixel misalignment.
   */
  width: number;
  /** Card height in px — used for CSS height and the <img> HTML attr. */
  height: number;
}

function CardTile({ card, width, height }: CardTileProps) {
  const [imgError, setImgError] = useState(false);

  return (
    // width:100% fills the CSS-grid cell exactly. height is explicit to maintain
    // the portrait aspect ratio regardless of image intrinsic dimensions.
    <div style={{ position: 'relative', width: '100%', height, containerType: 'inline-size' }}>
      {imgError || !card.imageUrl ? (
        // Placeholder fill: glassBorder.strong (rgba(255,255,255,0.35)) — 3.18:1 contrast
        // on the dark overlay surface, meeting WCAG 1.4.11 non-text contrast (≥3:1).
        <div style={{
          width: '100%',
          height,
          background: glassBorder.strong,
          borderRadius: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }} />
      ) : (
        <img
          src={card.imageUrl}
          alt={card.name}
          width={Math.round(width)}
          height={Math.round(height)}
          loading="eager"
          style={{ display: 'block', width: '100%', height, borderRadius: 3, objectFit: 'cover' }}
          onError={() => setImgError(true)}
        />
      )}
      {card.quantity > 1 && (
        // Matches canonical CardQuantityBadge: glass surface, blur, border, x{N} text.
        // Position: top:'20%', right:3 — matches the canonical badge position used in
        // DeckCardTile.tsx (top:'20%', right:1), SearchResultCard.tsx, and public-decks/CardTile.tsx.
        <div style={{
          position: 'absolute',
          top: '20%',
          right: 3,
          background: glass.surfaceBadgeSolid,
          border: `1px solid ${glassBorder.light}`,
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'clamp(2px, 3cqi, 5px)',
        }}>
          <span style={{
            fontWeight: 700,
            color: glassText.primary,
            lineHeight: 1,
            fontSize: 'clamp(10px, 8cqi, 13px)',
          }}>
            x{card.quantity}
          </span>
        </div>
      )}
    </div>
  );
}
